/**
 * 布局预设与垂直空间求解（docs/UI设计.md §2）
 *
 * 图表区要容纳 legend + 主图 + 最多 3 个副图，垂直空间紧张。
 * 这里把「给定容器高度与副图数，算出各部分高度」的规则集中一处，
 * 供图表区与各视图共用，避免逻辑分散。
 */

/** 硬约束：主图最小高度（px） */
export const MAIN_CHART_MIN = 200;
/**
 * 图表读数条高度（px）。
 *
 * 它取代了原来的 `LEGEND_HEIGHT`（26px）。改后读数条要显示光标所在柱的
 * OHLC + 涨跌 + 未收盘角标，单行 26px 放不下。
 *
 * ⚠️ 高度预算必须与本常量同步：`solveChartSpace` 拿到的可用高度里要扣掉它，
 *    否则读数条撑高容器会挤压绘图区（图表底部被裁）。
 *    这类"固定高度与实测高度不一致导致下溢"的坑已发生过一次，故加单测钉住。
 */
export const READOUT_HEIGHT = 42;
/** 读数条仅一行，容器高度固定，无需再单列图例高度 */
export const LEGEND_HEIGHT = READOUT_HEIGHT;
/** 副图高度区间（px） */
export const PANE_MIN = 56;
export const PANE_MAX = 140;
/** 副图数量上限 —— 与需求 F2.6 一致 */
export const PANE_MAX_COUNT = 3;

/**
 * 布局预设 —— 语义从「图表占比比例」改为「**图表高度档位（px）**」。
 *
 * 改前是可拖拽的 SplitPane（ratio 0.4–0.75），改为整页滚动后图表不再随窗口
 * 填满，分隔条失去意义；拖拽也要求图表区有固定像素高度，而 lightweight-charts
 * 的 canvas 正是**必须**有确定高度才渲染（不认百分比）。
 *
 * ⚠️ **id 故意沿用旧值**（balanced/chart/writing/focus）：用户的
 *    `localStorage['layout.presetId']` 里存的就是这些 id，改名会导致
 *    读不到而回落到默认档。`chartRatio` 同理保留但不再参与计算。
 */
export interface LayoutPreset {
  id: string;
  label: string;
  /** 图表区域高度（px）—— 容器总高，含读数条 */
  chartHeightPx: number;
  /** @deprecated 仅用于读取旧偏好，比例已不参与计算 */
  chartRatio: number;
}

export const LAYOUT_PRESETS: LayoutPreset[] = [
  { id: 'balanced', label: '均衡', chartHeightPx: 400, chartRatio: 0.6 },
  { id: 'chart', label: '图表优先', chartHeightPx: 520, chartRatio: 0.75 },
  { id: 'writing', label: '写作模式', chartHeightPx: 300, chartRatio: 0.38 },
  { id: 'focus', label: '工作区优先', chartHeightPx: 200, chartRatio: 0.16 },
];

export const DEFAULT_PRESET = LAYOUT_PRESETS[0];

/**
 * 窄屏图表高度：取视口的 46%。
 *
 * 移动端图表与工作区**同页滚动**（不再有底部分栏），图表若固定 400px
 * 在 640px 高的手机上会占掉大半屏，挤掉下面的笔记/新闻。
 * 46% 兼顾「看得见图」与「看得到下方内容」。
 */
export const MOBILE_CHART_HEIGHT_VH = 0.46;

/** 预设按 id 查找；找不到（老偏好或非法值）时回落到默认档 */
export function findPreset(id: string | null | undefined): LayoutPreset {
  return LAYOUT_PRESETS.find((p) => p.id === id) ?? DEFAULT_PRESET;
}

export interface ChartSpace {
  mainHeight: number;
  paneHeight: number;
  /** 空间不足（副图被压到下限后主图仍不够），UI 应提示用户 */
  cramped: boolean;
  /** 副图是否超出数量上限 */
  overflow: boolean;
}

/**
 * 求解图表区内部的高度分配（docs/UI设计.md §2）。
 *
 *   paneH = clamp(PANE_MIN, (H − LEGEND − MAIN_CHART_MIN) / n, PANE_MAX)
 *   mainH = H − LEGEND − paneH × n
 *   约束：mainH ≥ MAIN_CHART_MIN，副图数 ≤ PANE_MAX_COUNT
 *
 * 输入 available 是**主图与副图容器合计**的高度（不含图例行）。
 * 空间不足以同时容纳 n 个副图时，优先压缩副图并允许主图降级，
 * 但绝不下探到 0 —— 归零会让图表完全消失且无法自恢复。
 */
export function solveChartSpace(available: number, paneCount: number): ChartSpace {
  const n = Math.max(0, paneCount);
  const overflow = n > PANE_MAX_COUNT;

  const usable = Math.max(0, available);
  if (n === 0) {
    return { mainHeight: usable, paneHeight: 0, cramped: usable < MAIN_CHART_MIN, overflow };
  }

  const rawPane = (usable - MAIN_CHART_MIN) / n;
  const paneHeight = Math.min(PANE_MAX, Math.max(PANE_MIN, Math.floor(rawPane)));

  // 副图先让位给主图：不足以容纳 n 个完整副图时，逐个削减
  let remaining = usable;
  let mainHeight: number;
  let actualPanes = n;
  while (actualPanes > 0 && remaining - paneHeight * actualPanes < MAIN_CHART_MIN) {
    actualPanes -= 1;
  }

  if (actualPanes === 0) {
    // 连一个副图都放不下时，主图独占，副图让位（UI 层据此隐藏副图）
    mainHeight = usable;
  } else {
    remaining -= paneHeight * actualPanes;
    mainHeight = Math.max(MAIN_CHART_MIN, remaining);
  }

  return {
    mainHeight,
    paneHeight: actualPanes > 0 ? paneHeight : 0,
    cramped: mainHeight < MAIN_CHART_MIN || actualPanes < n,
    overflow,
  };
}

export const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));